import { memo, useContext, useEffect, useState } from "react"
import { useFragment } from "react-relay"
import { Card } from "@/components/ui/card"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import {
  EpisodeOfCareFragment$key,
  EpisodeOfCareNode,
} from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { useIsProgressNoteEditable } from "@/modules/patients/detail/progress-note/hooks/useIsProgressNoteEditable"
import { ProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { ParentEpisodeOfCareProvider } from "@/modules/patients/detail/progress-note/point-of-care/point-of-care-context"
import { ProgressNoteCardHeader } from "@/modules/patients/detail/progress-note/progress-note-card-header"
import { ProgressNoteEditableCardBody } from "@/modules/patients/detail/progress-note/progress-note-editable-card-body"
import { ProgressNoteReadOnlyCardBody } from "@/modules/patients/detail/progress-note/progress-note-readonly-card-body"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { PatientContextAppointmentFragment$data } from "@/modules/patients/services/__generated__/PatientContextAppointmentFragment.graphql"
import { Country } from "@/modules/patients/services/__generated__/PatientFragment.graphql"

export interface ProgressNoteAndParentData {
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  parentEpisodeOfCare: EpisodeOfCareNode
}

interface ProgressNoteCardProps {
  patientId: string
  appointmentContext: PatientContextAppointmentFragment$data | null | undefined
  isEditMode?: boolean
  parentEpisodeOfCare: EpisodeOfCareNode
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  country: Country
}

const ProgressNoteCard = memo(function ProgressNoteCard({
  patientId,
  appointmentContext,
  isEditMode,
  parentEpisodeOfCare,
  episodeOfCareProgressNoteData,
  country,
}: ProgressNoteCardProps) {
  const ProgressNoteFormStore = useContext(ProgressNoteFormContext)
  if (!ProgressNoteFormStore) throw new Error("Missing ProgressNoteFormProvider in the tree")

  const [isEditWithin24HoursAfterCloseMode, setIsEditWithin24HoursAfterCloseMode] = useState(() => {
    return ProgressNoteFormStore.getState().progressNoteForms.has(episodeOfCareProgressNoteData.id)
  })

  useEffect(() => {
    if (!isEditMode) setIsEditWithin24HoursAfterCloseMode(false)
  }, [isEditMode, setIsEditWithin24HoursAfterCloseMode])

  const isProgressNoteEditable = useIsProgressNoteEditable(episodeOfCareProgressNoteData)

  const { isUpdatable: isEpisodeOfCareUpdatable } = useFragment<EpisodeOfCareFragment$key>(
    getEpisodeOfCareFragment(country),
    parentEpisodeOfCare,
  )
  const { diagnosisCode } = parentEpisodeOfCare.icdDiagnosis
  return (
    <Card id={episodeOfCareProgressNoteData.id} data-testid="progress-note-item">
      <ProgressNoteCardHeader
        country={country}
        parentEpisodeOfCareFragmentReference={parentEpisodeOfCare}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        isEditMode={isEditMode}
        isEpisodeOfCareUpdatable={isEpisodeOfCareUpdatable}
        isEditWithin24HoursAfterCloseMode={isEditWithin24HoursAfterCloseMode}
        onEditWithin24HoursAfterCloseModeChange={setIsEditWithin24HoursAfterCloseMode}
      />
      {isEditMode &&
      appointmentContext &&
      ((isEpisodeOfCareUpdatable && isProgressNoteEditable) || isEditWithin24HoursAfterCloseMode) ? (
        <ParentEpisodeOfCareProvider parentEpisodeOfCare={parentEpisodeOfCare}>
          <ProgressNoteEditableCardBody
            patientId={patientId}
            appointmentContext={appointmentContext}
            episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
            diagnosisCode={diagnosisCode}
          />
        </ParentEpisodeOfCareProvider>
      ) : (
        <ProgressNoteReadOnlyCardBody progressNote={episodeOfCareProgressNoteData} />
      )}
    </Card>
  )
}, areProgressNoteCardPropsEqual)

function areProgressNoteCardPropsEqual(oldProps: ProgressNoteCardProps, newProps: ProgressNoteCardProps) {
  return (
    oldProps.isEditMode === newProps.isEditMode &&
    oldProps.country === newProps.country &&
    oldProps.episodeOfCareProgressNoteData === newProps.episodeOfCareProgressNoteData
  )
}

export { ProgressNoteCard }
